import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';

const OnePageFooter = () => {
  const { t, isRTL } = useLanguage();

  const quickLinks = [
    { section: 'technology', label: t('nav.technology') },
    { section: 'services', label: t('nav.services') },
    { section: 'footprint', label: t('nav.trackRecord') },
    { section: 'contact', label: t('nav.contact') },
  ];

  const handleQuick = (section) => {
    window.dispatchEvent(new CustomEvent('onepage-scroll', { detail: section }));
  };

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="block w-max mx-auto mb-6">
              <img
                src="/images/petro_logo_shape_only.png"
                alt={t('footer.logoAlt')}
                className="h-14 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">{t('footer.companyIntro')}</p>
          </div>

          {/* Quick Links (scroll within one-page) */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t('footer.quickLinksLabel')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.section}>
                  <button
                    onClick={() => handleQuick(link.section)}
                    className="text-gray-400 hover:text-red-500 transition-colors text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Headquarters */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t('contact.hqTitle')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="h-4 w-4 mt-1 text-red-500 flex-shrink-0" />
                <span>{t('contact.hqAddress')}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="leading-tight">
                  {t('contact.hqPhone')
                    .split('/')
                    .map((s, i) => (
                      <span key={i} className="block" dir="ltr">
                        {s.trim()}
                      </span>
                    ))}
                </span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="leading-tight">
                  {t('contact.hqEmail')
                    .split('/')
                    .map((s, i) => (
                      <span key={i} className="block">
                        {s.trim()}
                      </span>
                    ))}
                </span>
              </li>
            </ul>
          </div>

          {/* Representative Office */}
          <div>
            <h3 className="text-white font-semibold mb-6">{t('contact.repOfficeTitle')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="h-4 w-4 mt-1 text-red-500 flex-shrink-0" />
                <span>{t('contact.repOfficeAddress')}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="leading-tight">
                  {t('contact.repOfficePhone')
                    .split('/')
                    .map((s, i) => (
                      <span key={i} className="block" dir="ltr">
                        {s.trim()}
                      </span>
                    ))}
                </span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="leading-tight">
                  {t('contact.repOfficeEmail')
                    .split('/')
                    .map((s, i) => (
                      <span key={i} className="block">
                        {s.trim()}
                      </span>
                    ))}
                </span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Globe className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span>{t('contact.repOfficeWebsite')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-zinc-800 flex justify-center">
          <p className="text-gray-500 text-sm text-center">{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default OnePageFooter;
